import { useState, useEffect, useRef, useCallback } from "react";

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  read: boolean;
  created_at: string;
}

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffSec = Math.floor((now - then) / 1000);
  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDays = Math.floor(diffHr / 24);
  return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
}

export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  const fetchCount = useCallback(() => {
    fetch("/api/notifications/count", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) setCount(data.count);
      })
      .catch(() => {});
  }, []);

  const fetchNotifications = useCallback(() => {
    fetch("/api/notifications", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) setNotifications(data);
      })
      .catch(() => {});
  }, []);

  // Poll count every 30s
  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => clearInterval(id);
  }, [fetchCount]);

  // Load notifications when dropdown opens
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markRead = useCallback(
    (n: Notification) => {
      if (!n.read) {
        fetch(`/api/notifications/${n.id}/read`, {
          method: "POST",
          credentials: "include",
        }).then(() => {
          setNotifications((prev) =>
            prev.map((x) => (x.id === n.id ? { ...x, read: true } : x))
          );
          setCount((c) => Math.max(0, c - 1));
        });
      }
      if (n.link) {
        window.location.href = n.link;
      }
    },
    []
  );

  const markAllRead = useCallback(() => {
    fetch("/api/notifications/read-all", {
      method: "POST",
      credentials: "include",
    }).then(() => {
      setNotifications((prev) => prev.map((x) => ({ ...x, read: true })));
      setCount(0);
    });
  }, []);

  return (
    <div className="notification-bell" ref={ref}>
      <button
        className="notification-bell-icon"
        onClick={() => setOpen((v) => !v)}
        title="Notifications"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M8 1.5a4 4 0 0 0-4 4v3l-1.5 2h11L12 8.5v-3a4 4 0 0 0-4-4z" />
          <path d="M6.5 13.5a1.5 1.5 0 0 0 3 0" />
        </svg>
        {count > 0 && (
          <span className="notification-badge">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div className="notification-dropdown">
          <div
            style={{
              padding: "10px 12px 8px",
              fontWeight: 600,
              fontSize: 13,
              color: "var(--text-bright)",
              borderBottom: "1px solid var(--border-subtle)",
            }}
          >
            Notifications
          </div>

          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {notifications.length === 0 && (
              <div
                style={{
                  padding: "20px 12px",
                  textAlign: "center",
                  color: "var(--text-muted)",
                  fontSize: 12,
                }}
              >
                No notifications
              </div>
            )}
            {notifications.map((n) => (
              <div
                key={n.id}
                className={`notification-item ${!n.read ? "notification-item--unread" : ""}`}
                onClick={() => markRead(n)}
              >
                <div className="notification-title">{n.title}</div>
                {n.body && (
                  <div className="notification-body">{n.body}</div>
                )}
                <div className="notification-time">
                  {timeAgo(n.created_at)}
                </div>
              </div>
            ))}
          </div>

          {notifications.length > 0 && (
            <button className="notification-mark-all" onClick={markAllRead} title="Mark all notifications as read">
              Mark all as read
            </button>
          )}
        </div>
      )}
    </div>
  );
}
